import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Button,
  ButtonGroup,
  Col,
  Form,
  Row,
  Spinner,
  Stack,
} from 'react-bootstrap';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import {
  faClose,
  faPenToSquare,
  faRefresh,
  faTrash,
  faUserPlus,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { debounce } from 'lodash';
import {
  APP_EVENTS,
  captureAppEvent,
  TABLE_CONTEXT,
} from 'src/services/posthogEvents';

import useLocalStore from 'src/store/useLocalStore';

import ColumnConfiguration from 'src/components/custom_components/ColumnConfiguration';
import CustomInputGroupWithIcon from 'src/components/custom_components/customInputGroupWithIcon';
import CustomTable, {
  Column,
} from 'src/components/custom_components/CustomTable/CustomTable';

import { Api } from 'src/models/Api.model';
import { DEFAULT_PAGE_SIZE } from 'src/models/DefaultPageSize.model';
import { CustomUserProfileFieldEnum } from 'src/models/GalenData/CustomField.model';
import { CustomFieldData } from 'src/models/GalenData/CustomFieldData.model';
import { GalenDataUser } from 'src/models/GalenData/User.model';
import { I18NextNS } from 'src/models/Language.model';

import useColumnVisibility from 'src/hooks/useColumnVisibility';
import useGetCurrentUser from 'src/hooks/useGetCurrentUser';
import useGetUsers from 'src/hooks/useGetUsers';
import useHandleHttpRequestError from 'src/hooks/useHandleHttpRequestError';

import formatUserRole from 'src/utils/formatUserRole';

import AddUserFormModal from './AddUserFormModal';
import EditUserFormModal from './EditUserFormModal';
const SubUserTab = () => {
  const { isGettingUsers, handleGetUsers, users } = useGetUsers();

  const [showAddUserModal, setShowAddUserModal] = useState(false);
  const [currentUser, setCurrentUser] = useState<GalenDataUser | null>(null);
  const [loadingUserId, setLoadingUserId] = useState<string | null>(null);
  const [userSettingsData, setUserSettingsData] =
    useState<CustomFieldData<CustomUserProfileFieldEnum>[]>();

  const [currentPage, setCurrentPage] = useState<number>(1);

  const {
    t,
    i18n: { language },
  } = useTranslation(I18NextNS.PAGES);

  const { userEmail } = useGetCurrentUser();

  const { showHttpRequestError } = useHandleHttpRequestError();

  const handleShowAddUserModal = () => {
    setCurrentUser(null);
    setShowAddUserModal(true);
  };

  const handleCloseAddUserModal = () => {
    setShowAddUserModal(false);
  };

  const fetchUserSettings = useCallback(
    async (userId: string) => {
      try {
        const { data } = await Api.remote.getUserSettings(userId);
        return data;
      } catch (error) {
        showHttpRequestError(error);
      }
    },
    [showHttpRequestError],
  );

  const handleShowEditUserModal = useCallback(
    async (user: GalenDataUser) => {
      try {
        setLoadingUserId(user.userId);
        setCurrentUser(user);
        const data = await fetchUserSettings(user.userId);
        setUserSettingsData(data);
      } catch (error) {
        showHttpRequestError(error);
      } finally {
        setLoadingUserId(null);
      }
    },
    [fetchUserSettings, showHttpRequestError],
  );

  const handleCloseEditUserModal = () => {
    setUserSettingsData(undefined);
  };

  const {
    register,
    setValue,
    getValues,
    reset,
    formState: { isDirty },
  } = useForm({
    defaultValues: {
      emailAddress: '',
      name: '',
    },
  });

  const initializeUsersList = useCallback(async () => {
    await handleGetUsers(DEFAULT_PAGE_SIZE);
    setCurrentPage(1);
    reset();
  }, [handleGetUsers, reset]);

  useEffect(() => {
    initializeUsersList();
  }, [initializeUsersList]);

  const handleDeleteUser = useCallback(
    (user: GalenDataUser) => {
      Api.confirmBox({
        title: t('bootbox.title.Confirm', { ns: I18NextNS.COMMON }),
        message: t('users.deleteUserMessage', {
          emailAddress: user.emailAddress,
          fullName: user.fullName,
        }),
        locale: language,
        callback: async (yes) => {
          if (!yes) {
            return;
          }

          try {
            await Api.remote.delete_user(user.userId);

            captureAppEvent(APP_EVENTS.USER_DELETED);

            initializeUsersList();

            Api.alertBox({
              title: t('bootbox.title.Success', { ns: I18NextNS.COMMON }),
              message: t('users.deleteUserSuccessMessage', {
                emailAddress: user.emailAddress,
                fullName: user.fullName,
              }),
              locale: language,
              type: 'success',
            });
          } catch (error) {
            showHttpRequestError(error);
          }
        },
      });
    },
    [initializeUsersList, showHttpRequestError, language, t],
  );

  const columns: Column<GalenDataUser>[] = useMemo(
    () => [
      { title: t('users.loginUsername'), key: 'emailAddress' },
      { title: t('users.fullName'), key: 'fullName' },
      {
        title: t('users.userType'),
        dataIndex: 'userType',
        render: (record) => (
          <div>
            {record.roles.map((role, index) => (
              <ul key={`${role.role}${index}`}>
                <li>
                  {role.role &&
                    t(
                      `patientFields.userRoleEnum.${formatUserRole(role.role)}`,
                      {
                        ns: I18NextNS.COMMON,
                      },
                    )}{' '}
                  {role.practice?.name && `(${role.practice.name})`}
                </li>
              </ul>
            ))}
          </div>
        ),
      },
      {
        title: t('users.organization'),
        dataIndex: 'organization',
        render: (record) => <div>{record.currentRole?.practice?.name}</div>,
      },
      {
        title: t('users.actions'),
        dataIndex: 'actions',
        render: (record) => {
          return (
            <ButtonGroup className="gap-2">
              <Button
                variant="outline-primary"
                className="rounded-3 px-3 py-1"
                data-test="edit-member-button"
                onClick={() => {
                  handleShowEditUserModal(record);
                }}
                disabled={loadingUserId === record.userId}
                data-track="users.subuser.edit"
                data-observe
              >
                {loadingUserId === record.userId ? (
                  <Spinner
                    as="span"
                    animation="border"
                    size="sm"
                    role="status"
                    aria-hidden="true"
                  />
                ) : (
                  <FontAwesomeIcon icon={faPenToSquare} className="me-0" />
                )}
              </Button>
              <Button
                variant="outline-danger"
                className="rounded-3 px-3 py-1"
                data-test="delete-member-button"
                onClick={() => {
                  handleDeleteUser(record);
                }}
                hidden={record.emailAddress === userEmail}
                data-track="users.subuser.delete"
                data-observe
              >
                <FontAwesomeIcon icon={faTrash} className="me-0" />
              </Button>
            </ButtonGroup>
          );
        },
      },
    ],
    [handleDeleteUser, handleShowEditUserModal, t, loadingUserId, userEmail],
  );

  const { usersColsKeys, setUsersColsKeys } = useLocalStore();

  const { visibleColumns, handleToggleColumn, displayedColumns } =
    useColumnVisibility(columns, usersColsKeys, setUsersColsKeys, {
      tableContext: TABLE_CONTEXT.USERS,
    });

  const debouncedSearch = useMemo(
    () =>
      debounce(() => {
        const emailAddress = getValues('emailAddress');
        const name = getValues('name');
        captureAppEvent(APP_EVENTS.SEARCH_FILTER_APPLIED, {
          table: TABLE_CONTEXT.USERS,
          has_email: Boolean(emailAddress?.trim()),
          has_name: Boolean(name?.trim()),
        });
        handleGetUsers(DEFAULT_PAGE_SIZE, 0, {
          emailAddress,
          name,
        });
        setCurrentPage(1);
      }, 1000),
    [handleGetUsers, getValues],
  );

  return (
    <Stack gap={3} className="h-100">
      <Row>
        <Col xl={4}>
          <h3>{t('users.title')}</h3>
          <div className="text-primary d-flex gap-2 align-items-center">
            <FontAwesomeIcon icon={faPenToSquare} />
            {t('users.edit')}
            <span>|</span>
            <FontAwesomeIcon icon={faTrash} />
            {t('users.delete')}
          </div>
        </Col>
        <Col
          xl={8}
          className="d-xl-flex justify-content-end align-items-center"
        >
          <Button
            variant="primary"
            className="d-inline-block ms-3 py-3"
            data-test="add-member-button"
            onClick={handleShowAddUserModal}
            data-track="users.subuser.add"
            data-observe
          >
            <FontAwesomeIcon
              icon={faUserPlus}
              className="me-2"
            ></FontAwesomeIcon>
            {t('users.add')}
          </Button>

          <Button
            variant="outline-primary"
            className="d-inline-block ms-3 py-3"
            onClick={initializeUsersList}
            data-track="users.subuser.reload"
            data-observe
          >
            <FontAwesomeIcon
              icon={faRefresh}
              className="me-2"
            ></FontAwesomeIcon>
            {t('users.reload')}
          </Button>
        </Col>
      </Row>

      <Row>
        <Stack direction="horizontal" gap={5} className="align-items-end">
          <Row className="flex-fill">
            <Col md={5}>
              <Form.Group as={Col} controlId="searchEmailAddress">
                <Form.Label>{t('users.loginUsername')}</Form.Label>
                <CustomInputGroupWithIcon>
                  <Form.Control
                    autoComplete="off"
                    type="text"
                    placeholder={t('users.loginUsername')}
                    {...register('emailAddress', {
                      onChange: (event) => {
                        setValue('emailAddress', event.target.value);
                        debouncedSearch();
                      },
                    })}
                  />
                </CustomInputGroupWithIcon>
              </Form.Group>
            </Col>

            <Col md={5}>
              <Form.Group as={Col} controlId="searchName">
                <Form.Label>{t('users.fullName')}</Form.Label>
                <CustomInputGroupWithIcon>
                  <Form.Control
                    autoComplete="off"
                    type="text"
                    placeholder={t('users.fullName')}
                    {...register('name', {
                      onChange: (event) => {
                        setValue('name', event.target.value);
                        debouncedSearch();
                      },
                    })}
                  />
                </CustomInputGroupWithIcon>
              </Form.Group>
            </Col>

            <Col md={2} className="d-flex align-items-end">
              <Button
                variant="outline-warning"
                onClick={initializeUsersList}
                disabled={!isDirty}
                data-testid="reset-button"
                className="py-2"
                data-track="users.subuser.reset-filters"
                data-observe
              >
                <FontAwesomeIcon icon={faClose} />
              </Button>
            </Col>
          </Row>
          <ColumnConfiguration
            columns={columns}
            visibleColumns={visibleColumns}
            onToggleColumn={handleToggleColumn}
          />
        </Stack>
      </Row>

      <Row className="flex-grow-1" style={{ minHeight: 0 }}>
        <Col className="d-flex flex-column h-100">
          <div className="flex-grow-1" style={{ minHeight: 0 }}>
            <CustomTable
              data-test="members-list"
              isLoading={isGettingUsers}
              dataSource={users.content ?? []}
              rowKey="userId"
              tableContext={TABLE_CONTEXT.USERS}
              columns={displayedColumns}
              rowProps={(): React.HTMLAttributes<HTMLTableRowElement> =>
                ({
                  'data-test': 'member-item',
                }) as React.HTMLAttributes<HTMLTableRowElement>
              }
              pagination={{
                defaultPageSize: DEFAULT_PAGE_SIZE,
                pageSizeOptions: [DEFAULT_PAGE_SIZE],
                totalPages: users.totalPages ?? 1,
                currentPage,
                handleSetCurrentPage: (page) => {
                  setCurrentPage(page);
                },
                onChangePage: (pageNumber, pageSize) => {
                  handleGetUsers(pageSize, pageNumber - 1, {
                    emailAddress: getValues('emailAddress'),
                    name: getValues('name'),
                  });
                },
              }}
              empty={{
                buttonText: t('users.addNewUser'),
                onButtonClick: handleShowAddUserModal,
              }}
            />
          </div>
        </Col>
      </Row>

      {currentUser && userSettingsData && (
        <EditUserFormModal
          handleGetUsers={handleGetUsers}
          show={!!setUserSettingsData}
          handleClose={handleCloseEditUserModal}
          user={currentUser}
          pageSize={DEFAULT_PAGE_SIZE}
          currentPage={currentPage}
          searchValues={{
            emailAddress: getValues('emailAddress'),
            name: getValues('name'),
          }}
          userSettingsData={userSettingsData}
        />
      )}
      <AddUserFormModal
        initializeUsersList={initializeUsersList}
        show={showAddUserModal}
        handleClose={handleCloseAddUserModal}
      />
    </Stack>
  );
};

export default SubUserTab;
